/**
 * The calendar's pure geometry (ticket #213): a Monday-first month
 * matrix over UTC days. Everything here is a pure function of its
 * inputs — the screen supplies Date.now() and the query supplies the
 * due items, and this module turns them into grid cells, the query
 * window the matrix implies, and the month-walking arithmetic the
 * prev/next arrows and the Today button drive.
 *
 * All day arithmetic is UTC, matching the stored dueAt convention
 * (date-only UTC midnights — see shared dueDateToMs) and the badge
 * logic (shared dueInfo buckets by UTC day too). Buckets still floor
 * arbitrary instants correctly, so a mid-day dueAt rings its own day.
 */

export const DAY_MS = 86_400_000;

/** The weekday header, Monday-first — the grid's column labels. */
export const WEEKDAY_HEADER = ["M", "T", "W", "T", "F", "S", "S"] as const;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/** A calendar month; `month` is 0-based like Date's UTC months. */
export type Month = { year: number; month: number };

/** One cell of the grid. `dayMs` is the cell's UTC-midnight identity. */
export type CalendarDay = {
  dayMs: number;
  /** `YYYY-MM-DD` — the stable testID suffix and debug shape. */
  iso: string;
  dayNumber: number;
  /** False for leading/trailing adjacent-month cells. */
  inMonth: boolean;
};

export type MonthMatrix = {
  month: Month;
  title: string;
  /** 4–6 Monday-first weeks of 7 cells each. */
  weeks: CalendarDay[][];
};

/** Any instant → its UTC day's midnight (the ring key). */
export function utcDayStart(ms: number): number {
  return Math.floor(ms / DAY_MS) * DAY_MS;
}

/** The UTC month an instant belongs to. */
export function monthOf(ms: number): Month {
  const d = new Date(ms);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
}

/** Walk `delta` months (negative walks back), unbounded both ways. */
export function walkMonth({ year, month }: Month, delta: number): Month {
  const zero = year * 12 + month + delta;
  return { year: Math.floor(zero / 12), month: ((zero % 12) + 12) % 12 };
}

export function monthTitle({ year, month }: Month): string {
  return `${MONTH_NAMES[month]} ${year}`;
}

/** (getUTCDay is 0=Sunday; the grid's first column is Monday.) */
const mondayOffset = (ms: number) => (new Date(ms).getUTCDay() + 6) % 7;

const isoDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * The month's grid: leading/trailing adjacent-month cells fill the first
 * and last weeks so every row is a complete Monday–Sunday week. Row
 * count is whatever the month needs — 4 (a 28-day month starting
 * Monday) through 6.
 */
export function monthMatrix(m: Month): MonthMatrix {
  const firstOfMonth = Date.UTC(m.year, m.month, 1);
  const firstCell = firstOfMonth - mondayOffset(firstOfMonth) * DAY_MS;
  // Day 0 of next month = this month's last day.
  const daysInMonth = new Date(Date.UTC(m.year, m.month + 1, 0)).getUTCDate();
  const cellCount = mondayOffset(firstOfMonth) + daysInMonth;
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < cellCount; i += 7) {
    const week: CalendarDay[] = [];
    for (let j = 0; j < 7; j++) {
      const dayMs = firstCell + (i + j) * DAY_MS;
      week.push({
        dayMs,
        iso: isoDate(dayMs),
        dayNumber: new Date(dayMs).getUTCDate(),
        inMonth: new Date(dayMs).getUTCMonth() === m.month,
      });
    }
    weeks.push(week);
  }
  return { month: m, title: monthTitle(m), weeks };
}

/**
 * The query window the matrix implies: the first cell's midnight to
 * JUST past the last cell's (end-exclusive). Filling adjacent-month
 * cells needs their days inside one window, so the window is the
 * matrix's, not the month's.
 */
export function matrixWindow(weeks: CalendarDay[][]): {
  startMs: number;
  endMs: number;
} {
  const flat = weeks.flat();
  return {
    startMs: flat[0]!.dayMs,
    endMs: flat[flat.length - 1]!.dayMs + DAY_MS,
  };
}
